import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pre-fetch backend quote prices for a single product combo at all
 * "standard" quantity tiers (1, 5, 10, 25, 50, 100, 250, 500, 1000,
 * 2500, 5000, 10000) in one parallel burst.
 *
 * Why: the Quote Calculator shows a synchronous price table (Step 5) and
 * a live price preview (useMemo) — both need instant answers. Firing an
 * async HTTP call on every input tick would make the UI sluggish and
 * burn Freightcom/pricing roundtrips. Instead we fetch the full tier
 * column once whenever product/size/tier changes, cache the answers,
 * and let the render layer do synchronous cache lookups + log-linear
 * interpolation for custom quantities.
 *
 * Returns:
 *   - `lookup(qty)`: synchronous function — returns the CAD price for
 *     `qty`, interpolating log-linearly when qty isn't a tier; returns
 *     `null` while loading or when the combo has no data.
 *   - `status`: 'idle' | 'loading' | 'ready' | 'error'
 *   - `error`: backend error message or null
 *
 * The hook always returns CAD; callers handle CAD↔USD conversion via
 * the currency dropdown (default cad_to_usd = 0.73).
 *
 * All prices are **stickeryou tier** (i.e., full price). The frontend
 * applies the Evergreen 30% off client-side when the user is on that
 * tier — matches the backend `price_for()` behavior (backend also holds
 * both tiers; we just pull the StickerYou one and discount in JS to
 * cut API load in half).
 */
const QTY_TIERS = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];

// Page combos use step pricing (no fractional volume discount between
// tiers). Mirrors the backend's _is_page_combo / step_mode logic so the
// synchronous client-side interpolation matches the authoritative
// backend price exactly for custom quantities. See
// /app/backend/utils/pricing_engine.py
const isPageCombo = (combo) =>
  !!(combo && combo.format && /page/i.test(combo.format));

const logLerp = (points, qty, stepMode = false) => {
  // points: sorted array of [q, p] pairs with q > 0 and p > 0
  if (!points.length) return null;
  if (points.length === 1) return points[0][1];
  const [qmin, pmin] = points[0];
  const [qmax, pmax] = points[points.length - 1];

  // Exact match
  const exact = points.find((pt) => pt[0] === qty);
  if (exact) return exact[1];

  // Below min / above max — linear per-unit extrapolation
  if (qty <= qmin) return (pmin * qty) / qmin;
  if (qty >= qmax) {
    const [q1, p1] = points[points.length - 2];
    const [q2, p2] = points[points.length - 1];
    const slope =
      (Math.log(p2) - Math.log(p1)) / (Math.log(q2) - Math.log(q1));
    return Math.exp(Math.log(p2) + slope * (Math.log(qty) - Math.log(q2)));
  }

  // Interior — log-linear within bracket, OR step-pricing for Page combos.
  for (let i = 0; i < points.length - 1; i += 1) {
    const [q1, p1] = points[i];
    const [q2, p2] = points[i + 1];
    if (q1 <= qty && qty <= q2) {
      if (stepMode) {
        // Linear step pricing: charge at the FLOOR tier's per-unit rate
        // until the next tier's catalog price is cheaper. Matches
        // pricing_engine._qty_price_for_row(step_mode=True) exactly.
        const stepPrice = (p1 * qty) / q1;
        return Math.min(stepPrice, p2);
      }
      const lp =
        Math.log(p1) +
        ((Math.log(p2) - Math.log(p1)) *
          (Math.log(qty) - Math.log(q1))) /
          (Math.log(q2) - Math.log(q1));
      return Math.exp(lp);
    }
  }
  return pmax;
};

export function useBackendPrices({ combo, widthIn, heightIn, tier = 'stickeryou', enabled = true }) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [tierPrices, setTierPrices] = useState({}); // { 25: 64.11, 50: 100.45, ... }
  // True when the backend flagged this size as outside its XLSX catalog
  // grid (below smallest tier or above largest). The UI surfaces a small
  // "Estimate" badge + "Request a firm quote" CTA in this mode.
  const [extrapolated, setExtrapolated] = useState(false);
  // Populated by the backend's 52" hard-envelope guard. Triggers the UI
  // to hide the auto-quote table and offer a custom-quote link instead.
  const [requiresCustomQuote, setRequiresCustomQuote] = useState(false);

  // Cache per (combo, wIn, hIn, tier) so remounts of the same combo are free.
  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);

  const cacheKey = combo
    ? `${combo.category}|${combo.format}|${combo.finish}|${widthIn}|${heightIn}|${tier}`
    : null;

  useEffect(() => {
    if (!enabled || !combo || !widthIn || !heightIn) {
      setStatus('idle');
      setTierPrices({});
      setError(null);
      setExtrapolated(false);
      setRequiresCustomQuote(false);
      return;
    }

    // Serve from cache if we've already fetched this combo
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setTierPrices(cached.prices);
      setExtrapolated(!!cached.extrapolated);
      setRequiresCustomQuote(!!cached.requiresCustomQuote);
      setStatus(cached.requiresCustomQuote ? 'custom-quote-required' : 'ready');
      return;
    }

    // Cancel any in-flight prior fetch
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('loading');
    setError(null);
    setExtrapolated(false);
    setRequiresCustomQuote(false);

    const body = {
      category: combo.category,
      format: combo.format,
      finish: combo.finish,
      width: widthIn,
      height: heightIn,
      tier,
    };

    Promise.all(
      QTY_TIERS.map((q) =>
        axios
          .post(
            `${API_URL}/api/pricing/calculate`,
            { ...body, quantity: q },
            { signal: controller.signal },
          )
          .then((r) => ({
            q,
            cad: r.data?.cad ?? null,
            extrapolated: !!r.data?.extrapolated,
          }))
          .catch((err) => {
            // 404 = this combo/size unavailable; upstream interpolation
            // will discard nulls. 422 = size exceeds the press envelope —
            // bubbled up as a dedicated `custom-quote-required` state so
            // the UI can route to a contact CTA. Anything else -> error.
            if (err?.response?.status === 422) {
              throw Object.assign(new Error('custom-quote-required'), {
                requiresCustomQuote: true,
                detail: err?.response?.data?.detail,
              });
            }
            if (err?.response?.status === 404) return { q, cad: null, extrapolated: false };
            throw err;
          }),
      ),
    )
      .then((results) => {
        if (controller.signal.aborted) return;
        const next = {};
        let anyExtrap = false;
        results.forEach(({ q, cad, extrapolated: ex }) => {
          if (cad !== null && cad > 0) next[q] = cad;
          if (ex) anyExtrap = true;
        });
        cacheRef.current.set(cacheKey, {
          prices: next,
          extrapolated: anyExtrap,
          requiresCustomQuote: false,
        });
        setTierPrices(next);
        setExtrapolated(anyExtrap);
        setStatus('ready');
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError') return;
        if (err?.requiresCustomQuote) {
          cacheRef.current.set(cacheKey, {
            prices: {},
            extrapolated: false,
            requiresCustomQuote: true,
          });
          setTierPrices({});
          setRequiresCustomQuote(true);
          setStatus('custom-quote-required');
          return;
        }
        const detail = err?.response?.data?.detail;
        const msg = (detail && typeof detail === 'object' ? detail.message : detail) || err?.message;
        setError(msg || 'Pricing unavailable');
        setStatus('error');
      });

    return () => controller.abort();
  }, [cacheKey, combo, widthIn, heightIn, tier, enabled]);

  const points = useMemo(
    () =>
      Object.entries(tierPrices)
        .map(([q, p]) => [Number(q), p])
        .sort((a, b) => a[0] - b[0]),
    [tierPrices],
  );

  const stepMode = useMemo(() => isPageCombo(combo), [combo]);

  const lookup = useCallback(
    (qty) => {
      if (!qty || qty <= 0) return null;
      if (!points.length) return null;
      return logLerp(points, qty, stepMode);
    },
    [points, stepMode],
  );

  return { lookup, status, error, tierPrices, extrapolated, requiresCustomQuote };
}

export default useBackendPrices;
